import { useState } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { mfaListFactors, mfaChallenge, mfaVerify } from '@/lib/supabase'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { captureEvent } from '@/lib/posthog'

interface MfaDisableDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDisabled?: () => void
}

export function MfaDisableDialog({ open, onOpenChange, onDisabled }: MfaDisableDialogProps) {
  const setMfaStatus = useAuthStore((s) => s.setMfaStatus)
  const refreshAal = useAuthStore((s) => s.refreshAal)
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (code.length !== 6) return
    setIsLoading(true)
    setError(null)
    try {
      // Re-verify TOTP first to bump the AMR timestamp so requireFreshAal2
      // (5 min window) on the backend passes.
      const factors = await mfaListFactors()
      const totpFactor = (factors?.all ?? [])
        .filter((f) => f.factor_type === 'totp')
        .find((f) => f.status === 'verified')
      if (!totpFactor) {
        throw new Error('No verified authenticator found')
      }
      const challenge = await mfaChallenge(totpFactor.id)
      await mfaVerify(totpFactor.id, challenge.id, code)

      // Persist the unenroll on the backend (which also wipes recovery codes).
      await api.post('/users/me/mfa-unenrolled', {})
      setMfaStatus({
        enrolled: false,
        enrolledAt: null,
        recoveryCodesRemaining: 0,
        required: useAuthStore.getState().mfaRequired,
      })
      await refreshAal()
      captureEvent('mfa_unenrolled')
      toast.success('Two-factor authentication disabled')
      onDisabled?.()
      onOpenChange(false)
      setCode('')
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "That code wasn't valid. Try again with the next code your app shows."
      )
      setCode('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setCode(digits)
    if (digits.length === 6 && !isLoading) {
      void handleSubmit()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Disable two-factor authentication</DialogTitle>
          <DialogDescription>
            Enter the 6-digit code from your authenticator app to confirm. After disabling, only
            your password will be required to sign in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="disable-code" className="text-meta">
              Verification code
            </Label>
            <Input
              id="disable-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              className="text-center text-lg tracking-widest font-mono"
              autoFocus
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              className="hover:text-destructive"
              onClick={() => void handleSubmit()}
              disabled={code.length !== 6 || isLoading}
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Disable
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
